import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, ArrowLeftRight, PartyPopper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBach } from "@/context/BachContext";
import { BACH_NAV } from "@/lib/bachConfig";
import { Button } from "@/components/ui/button";

function NavItems({ onNavigate, base = "/bach" }) {
  const rebase = (to) => base + to.slice(5);
  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-6">
      {BACH_NAV.map((group) => (
        <div key={group.section}>
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7A686C]/70">{group.section}</div>
          <div className="space-y-0.5">
            {group.items.map((it) => (
              <NavLink key={it.to} to={rebase(it.to)} end={it.end} onClick={onNavigate}
                data-testid={`bach-nav-${it.key}`}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${isActive ? "text-[#6B1D2F] bg-[#F9ECE8] border-l-2 border-[#6B1D2F] font-semibold" : "text-[#7A686C] hover:text-[#2A181C] hover:bg-[#F9ECE8]"}`}>
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-6">
      <div className="h-9 w-9 rounded-full bg-[#6B1D2F] flex items-center justify-center"><PartyPopper className="h-4 w-4 text-white" strokeWidth={2} /></div>
      <div className="leading-tight">
        <div className="text-base font-serif font-semibold text-[#2A181C]">Bachelorette</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B1D2F]">by PlanMyWedding</div>
      </div>
    </div>
  );
}

export default function BachLayout({ children, base = "/bach", demo = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { profile } = useBach();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const doLogout = async () => { navigate("/", { replace: true }); await logout(); };

  return (
    <div className="min-h-screen flex bg-[#FAF8F5]">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-[#EFE3DE] bg-[#FAF8F5] sticky top-0 h-screen z-10">
        <Brand /><NavItems base={base} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-[#FAF8F5] border-r border-[#EFE3DE] flex flex-col">
            <div className="flex items-center justify-between pr-3"><Brand /><Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} data-testid="bach-close-drawer"><X className="h-5 w-5" /></Button></div>
            <NavItems base={base} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col relative z-10">
        <header className="sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-8 h-16 border-b border-[#EFE3DE] bg-[#FAF8F5]/80 backdrop-blur-xl">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="bach-open-drawer"><Menu className="h-5 w-5" /></Button>
          {!demo && <button onClick={() => navigate("/planners")} data-testid="bach-switch-planner" className="flex items-center gap-2 text-sm text-[#6B1D2F] rounded-full border border-[#ECCDC5] bg-[#F9ECE8] px-4 py-2 hover:bg-[#ECCDC5] transition-colors">
            <ArrowLeftRight className="h-4 w-4" /> Switch planner
          </button>}
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-right leading-tight mr-1">
              <div className="text-xs font-medium text-[#2A181C] max-w-[160px] truncate">{profile?.party_name || "Bachelorette"}</div>
              <div className="text-[11px] text-[#7A686C] max-w-[160px] truncate">{user?.email}</div>
            </div>
            {!demo && <Button variant="ghost" size="icon" onClick={doLogout} data-testid="bach-logout" title="Log out"><LogOut className="h-[18px] w-[18px]" /></Button>}
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-8 py-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
